import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, Leaf, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const LOGO_WORDMARK = "/bloomers-wordmark.png";

const nav = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF8F5]/85 border-b border-[#21C078]/15"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo-link">
          <img src={LOGO_WORDMARK} alt="Bloomers" className="h-11 sm:h-12 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-[15px] font-bold transition-all ${
                  isActive
                    ? "bg-[#21C078] text-white"
                    : "text-[#1C1C1C]/80 hover:bg-[#21C078]/10 hover:text-[#1C1C1C]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-more-trigger"
              className="ml-1 px-4 py-2 rounded-full text-[15px] font-bold text-[#1C1C1C]/80 hover:bg-[#21C078]/10 flex items-center gap-1"
            >
              <Leaf className="w-4 h-4 text-[#21C078]" /> More
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-2xl border-[#21C078]/20 mt-2">
              <DropdownMenuItem asChild data-testid="dropdown-gallery">
                <Link to="/gallery">Browse Gallery</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="dropdown-contact">
                <Link to="/contact">Get a Consult</Link>
              </DropdownMenuItem>
              {user && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="dropdown-admin">
                    <Link to="/admin">Admin Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout();
                      navigate("/");
                    }}
                    data-testid="dropdown-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Link to="/contact" data-testid="navbar-cta-consult" className="btn-pill-primary">
            Consult Inquiry
          </Link>
        </div>

        <button
          className="md:hidden p-2 rounded-full text-[#1C1C1C]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden px-6 pb-6 border-t border-[#21C078]/10 bg-[#FAF8F5]/95">
          <div className="flex flex-col gap-1 pt-3">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-2xl font-bold ${
                    isActive ? "bg-[#21C078] text-white" : "text-[#1C1C1C]/80"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {user && (
              <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-nav-admin" className="px-4 py-3 rounded-2xl font-bold text-[#219DC0]">Admin</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
